import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router';
import {
  FaqContainer,
  AnswerContainer,
  AnswerText,
  AnswerBody,
  Section,
  QuestionsWrapper,
} from './style';
import { FAQ_GET } from '../../redux/actions';
import { CTA, NoResult, Text } from '../../../ui/atoms';
import HtmlRaw from '../../../ui/atoms/HtmlRaw';
import Icon from '../../../ui/atoms/Icon';
import { feBaseUrl } from '../../api/config';
import {
  getPageDescriptionByPath,
  getPageTitleByPath,
} from '../../../utils/labels';

const renderAnswer = (question, answer, callback, isOpen, category, index) => (
  <AnswerContainer key={`${category}-${index}`}>
    <AnswerText onClick={callback} isOpen={isOpen}>
      <Text type="h3">
        <HtmlRaw html={question} />
      </Text>
      {isOpen ? (
        <Icon type="chevron-down" rotate={180} />
      ) : (
        <Icon type="chevron-down" />
      )}
    </AnswerText>
    <AnswerBody isOpen={isOpen}>
      <Text type="b3">
        <HtmlRaw html={answer} />
      </Text>
    </AnswerBody>
  </AnswerContainer>
);

const Faq = ({ title, description, faqs, getFaq }) => {
  const { pathname } = useLocation();
  const [activeAnswer, setActiveAnswer] = useState();
  const [activeSections, setActiveSections] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    window.scrollTo(0, 0);
    getFaq();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClick = (index) => {
    if (activeAnswer === index) setActiveAnswer();
    else setActiveAnswer(index);
  };

  const isActiveSection = (index) => {
    return activeSections?.includes(index);
  };

  const handleSetActiveSection = (index) => {
    const newSections = [...activeSections];
    const sectionIndex = activeSections.findIndex((as) => as === index);
    if (sectionIndex >= 0) {
      newSections.splice(sectionIndex, 1);
    } else {
      newSections.push(index);
    }
    setActiveSections(newSections);
  };

  return (
    <>
      <Helmet>
        <title>{getPageTitleByPath(pathname)}</title>
        <link rel="canonical" href={`${feBaseUrl}${pathname}`} />
        <meta name="description" content={getPageDescriptionByPath(pathname)} />
      </Helmet>
      <FaqContainer>
        <Text type="h1" mb={16}>
          {title ?? 'Domande frequenti'}
        </Text>
        <Text type="b4" mb={32}>
          <HtmlRaw html={description} />
        </Text>
        <div>
          {faqs?.length > 0 ? (
            <>
              {faqs.map((section, i) => (
                <Section key={`section-${i}`}>
                  <div
                    className="title-wrapper"
                    onClick={() => handleSetActiveSection(i)}
                  >
                    <Text type="h2" mb={24}>
                      {section?.section_title}
                    </Text>
                    <CTA
                      type="tertiary"
                      btnText={isActiveSection(i) ? 'Chiudi' : 'Esplora'}
                      icon={isActiveSection(i) ? 'minus' : 'plus'}
                      iconSize={16}
                    />
                  </div>
                  <QuestionsWrapper isOpen={isActiveSection(i)}>
                    {(section?.faq_elements || []).map((data) =>
                      renderAnswer(
                        data.question,
                        data.answer,
                        () => handleClick(data.id),
                        activeAnswer === data.id,
                        data.category,
                        data.id
                      )
                    )}
                  </QuestionsWrapper>
                </Section>
              ))}
            </>
          ) : (
            <NoResult />
          )}
        </div>
      </FaqContainer>
    </>
  );
};

Faq.propTypes = {
  // HOC (connect, state)
  title: PropTypes.string,
  description: PropTypes.string,
  faqs: PropTypes.arrayOf(PropTypes.object),

  // HOC (connect, dispatch)
  getFaq: PropTypes.func.isRequired,
};

export default connect(
  (state) => {
    const { title, description, faq_elements: faqs = [] } = state.app.faq;
    return {
      title,
      description,
      faqs,
    };
  },
  (dispatch) => ({
    getFaq: () => dispatch({ type: FAQ_GET._REQUEST }),
  })
)(Faq);
